"use client";

import { Fragment, useRef, useState } from 'react'
import { Dialog, Disclosure, Popover, Transition } from '@headlessui/react'
import Image from "next/image"
import Link from "next/link"
import {
  Bars3Icon,
  BeakerIcon,
  BriefcaseIcon as BriefcaseOutlineIcon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  CommandLineIcon,
  XMarkIcon,
  TruckIcon,
  SignalIcon
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const navigation = [
  // { name: 'RFQ', href: '/request-for-quotation' },
  { name: 'Blog', href: '/blogs' },
  // { name: 'Contact Us', href: '/contact-us' },
]

const industries = [
  { name: 'Food and Beverage', href: '/food-and-beverage'},
  { name: 'Medical and Healthcare', href: '/medical-and-healthcare'},
  { name: 'Custom Tees and Polo', href: '/customtees-and-polo'},
  { name: 'Construction, Manufacturing and Engineering', href: '/construction-manufacturing-engineering'},
  { name: 'Cleanroom', href: '/cleanroom'}
]

const solutions = [
  { name: 'Order Management', description: 'Workwear Order Management Platform', href: '/order-management-platform', icon: ChartPieIcon },
  { name: 'RFID System', description: 'RFID Linen Management System', href: '/linen-management-system', icon: SignalIcon},
  { name: 'Inventory Management', description: 'Real-time IoT RFID Asset Management', href: '/inventory-management-solution', icon: CursorArrowRaysIcon },
  { name: 'Laundry Services', description: 'Cloud-based Laundry Management Solutions', href: '/laundry-services-singapore', icon: TruckIcon },
  { name: 'Cleanroom Laundry', description: 'Cleanroom Laundry with RFID tracking solutions', href: '/cleanroom-laundry-and-managed-rfid-garment-solutions', icon: BeakerIcon },
  { name: 'Custom Uniform Rental', description: 'Branded uniform rental with RFID tracking', href: '/custom-uniform-rental', icon: BriefcaseOutlineIcon },
  { name: 'Enterprise Program', description: 'Connect Your ERP with MF', href: '/enterprise-programme', icon: CommandLineIcon },

]

const products = [
  { name: 'Catalogue', href: '/catalog'},
  { name: 'Custom Corporate Uniforms', href: '/our-products'},
]

const timeoutDuration = 120

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const solutionButtonRef = useRef<any>([])
  const industriesButtonRef = useRef<any>([])
  const productsButtonRef = useRef<any>([])
  // const triggerRef = useRef<any>([])
  const timeOutRef = useRef<any>([])

  const handleEnter = (event, isOpen) => {
    clearTimeout(timeOutRef.current)
    !isOpen && event.current?.click()
  }

  const handleLeave = (event, isOpen) => {
    timeOutRef.current = setTimeout(() => {
      isOpen && event.current?.click()
    }, timeoutDuration)
  }
  return (
    <header className={`fixed top-0 w-full bg-white border-b border-gray-200 z-30 transition-all`}>
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 py-3 px-4 lg:px-8" aria-label="Global">
        <div className="flex sm:flex-1">
          <Link href="/" className="-m-1.5 p-1.5">
            {/* <span className="sr-only">Your Company</span> */}
            <Image className="h-4 lg:h-6 min-w-min w-auto" src="/logo.png" width={150} height={150} priority alt="MF Asia Logo" />
          </Link>
        </div>

        <div className="hidden sm:flex lg:gap-x-12">
          <Popover.Group className="hidden sm:flex mx-auto gap-x-4 lg:gap-x-12">
            <Popover className="relative">
              {({ open }) => {
                return (
                    <div onMouseEnter={() => handleEnter(industriesButtonRef, open)}
                          onMouseLeave={() => handleLeave(industriesButtonRef, open)}>
                      <Popover.Button ref={industriesButtonRef} className="flex items-center gap-x-1 text-sm lg:text-base font-semibold leading-6 text-gray-900 outline-0">
                        Industries
                        <ChevronDownIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute -left-8 top-full z-10 mt-3 w-56 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5">
                          {industries.map((item) => (
                            <Link
                              key={item.name}
                              href={item.href}
                              className="block rounded-lg py-2 px-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50 hover:text-blue-600"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </Popover.Panel>
                      </Transition>
                    </div>
                )
              }}
            </Popover>

            <Popover className="relative">
              {({ open }) => {
                return (
                  <>
                    <div onMouseEnter={() => handleEnter(solutionButtonRef, open)} 
                          onMouseLeave={() => handleLeave(solutionButtonRef, open)}>
                      <Popover.Button ref={solutionButtonRef} className="flex items-center gap-x-1 text-sm lg:text-base font-semibold leading-6 text-gray-900 outline-0">
                        Solutions
                        <ChevronDownIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
                          <div className="p-4">
                            {solutions.map((item) => (
                              <div
                                key={item.name}
                                className="group relative flex gap-x-6 rounded-lg p-4 text-sm leading-6 hover:bg-gray-50"
                              >
                                <div className="mt-1 flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                                  <item.icon className="h-6 w-6 text-gray-600 group-hover:text-blue-600" aria-hidden="true" />
                                </div>
                                <div className="flex-auto">
                                  <Link href={item.href} className="block font-semibold text-gray-900 hover:text-blue-600">
                                    {item.name}
                                    <span className="absolute inset-0" />
                                  </Link>
                                  <p className="mt-1 text-gray-600">{item.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </div>
                  </>
                )
              }}
            </Popover>

            <Popover className="relative">
              {({ open }) => {
                return (
                    <div onMouseEnter={() => handleEnter(productsButtonRef, open)}
                          onMouseLeave={() => handleLeave(productsButtonRef, open)}>
                      <Popover.Button ref={productsButtonRef} className="flex items-center gap-x-1 text-sm lg:text-base font-semibold leading-6 text-gray-900 outline-0">
                        Products
                        <ChevronDownIcon className="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute -left-8 top-full z-10 mt-3 w-56 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5">
                          {products.map((item) => (
                            <Link
                              key={item.name}
                              href={item.href}
                              className="block rounded-lg py-2 px-3 text-sm font-semibold leading-6 text-gray-900 hover:bg-gray-50 hover:text-blue-600"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </Popover.Panel>
                      </Transition>
                    </div>
                )
              }}
            </Popover>

            
            {navigation.map((item) => (
              <Link key={item.name} href={item.href} className="text-sm lg:text-base font-semibold leading-6 text-gray-900">
                {item.name}
              </Link>
            ))}
          </Popover.Group>
        </div>
        <div className="flex flex-1 items-center justify-end gap-x-6">
          {/* <a href="#" className="block text-sm font-semibold leading-6 text-gray-900">
            Log in
          </a> */}
          {/* <Link
            href={`${process.env.NEXT_PUBLIC_BASE_URL}`}
            className="hidden sm:block rounded-md bg-blue-600 py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Login
          </Link> */}
          <Link href={`${process.env.NEXT_PUBLIC_BASE_URL}`} className="block text-sm lg:text-base font-semibold leading-6 text-gray-900">
            Login
          </Link>
          <Link
            href="/contact-us"
            className="block rounded-md bg-blue-600 py-2 px-3 text-sm lg:text-base font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Contact Us
          </Link>
        </div>
        <div className="flex sm:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md px-2.5 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-7 w-7" aria-hidden="true" />
          </button>
        </div>
      </nav>
      <Dialog as="div" className="lg:hidden __variable_89a9b2 font-sans" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="z-50 fixed inset-y-0 right-0 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center gap-x-6">
            <Link href="#" className="-m-1.5 p-1.5">
              {/* <span className="sr-only">Your Company</span> */}
              <Image
                className="h-6 w-auto"
                src="/logo.png"
                width={150}
                height={150}
                alt="MF Asia logo"
              />
            </Link>
            <Link
              href="/contact-us"
              className="ml-auto rounded-md bg-blue-600 py-2 px-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Contact Us
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-5 flow-root">
            <div className="divide-y divide-gray-500/10">
              <Disclosure as="div" className="-mx-3">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 hover:bg-gray-50">
                      Industries
                      <ChevronDownIcon
                        className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                        aria-hidden="true"
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="mt-2 space-y-2">
                      {industries.map((item) => (
                        <Disclosure.Button
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {item.name}
                        </Disclosure.Button>
                      ))}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
              <Disclosure as="div" className="-mx-3">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 hover:bg-gray-50">
                      Solutions
                      <ChevronDownIcon
                        className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                        aria-hidden="true"
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="mt-2 space-y-2">
                      {solutions.map((item) => (
                        <Disclosure.Button
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {item.name}
                        </Disclosure.Button>
                      ))}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
              <Disclosure as="div" className="-mx-3">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 hover:bg-gray-50">
                      Products
                      <ChevronDownIcon
                        className={classNames(open ? 'rotate-180' : '', 'h-5 w-5 flex-none')}
                        aria-hidden="true"
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="mt-2 space-y-2">
                      {products.map((item) => (
                        <Disclosure.Button
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {item.name}
                        </Disclosure.Button>
                      ))}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    href={item.href}
                    className="-mx-3 block rounded-lg py-2 px-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="py-6">
                <Link
                  href={`${process.env.NEXT_PUBLIC_BASE_URL}`}
                  className="-mx-3 block rounded-lg py-2.5 px-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  Log in
                </Link>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  )
}
